// WBT.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './wbt.css';
import './wbt-light.css';
import { ensureWBTApiKey } from './WebserverUser';
import {
  createServiceUpload,
  defaultServicePipeline,
  loadServiceCatalogue,
  loadServiceJobs,
  openJobDownload,
} from '../shared/xomics-webserver-client';
import CentralApiGuide from '../shared/CentralApiGuide';
import WebserverDemoSection from '../shared/WebserverDemoSection';
import WebserverReportPanel from '../shared/WebserverReportPanel';

import logoDark from '../../WBTRisk_Darkmode.png';
import logoLight from '../../WBTRisk_Lightmode.png';
import baseLogo from '../../base_logo.png';
import baseLogoLight from '../../base_logo_light.png';

import docScreenshot1 from '../../p1.jpeg';
import docScreenshot2 from '../../p2.png';
import docScreenshot3 from '../../p3.png';
import docScreenshot4 from '../../p4.png';

const THEME_STORAGE_KEY = 'wbtws-theme-preference';
const SERVICE_CODE = 'wbtrisk';

// Apply theme immediately on script load to prevent flash
(function applyStoredThemeImmediately() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  let theme = 'dark';
  if (stored === 'light') theme = 'light';
  else if (stored === 'system') {
    theme = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  const root = document.documentElement;
  root.classList.remove('wbtws-theme-light', 'wbtws-theme-dark');
  root.classList.add(theme === 'light' ? 'wbtws-theme-light' : 'wbtws-theme-dark');
})();

function WBT() {
  const [apiKey, setApiKey] = useState('');
  const [theme, setTheme] = useState('dark');
  const [activeTab, setActiveTab] = useState('home');

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // New job form state
  const [sampleName, setSampleName] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [selectedRead1, setSelectedRead1] = useState(null);
  const [selectedRead2, setSelectedRead2] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Polling ref
  const pollIntervalRef = useRef(null);
  const read1InputRef = useRef(null);
  const read2InputRef = useRef(null);

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Initialize API key and fetch data
  useEffect(() => {
    const init = async () => {
      const key = await ensureWBTApiKey();
      setApiKey(key);
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored) setTheme(stored);
    };
    init();
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // Fetch jobs when API key is ready
  useEffect(() => {
    if (apiKey) {
      loadServiceCatalogue(SERVICE_CODE, apiKey)
        .then((catalogue) => setPipeline(defaultServicePipeline(catalogue)))
        .catch((error) => console.error('Failed to load WBTRisk pipeline:', error));
      fetchJobs();
      startJobPolling();
    }
  }, [apiKey]);

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    try {
      if (!apiKey) return;
      const data = await loadServiceJobs(SERVICE_CODE, apiKey, { limit: 50 });
      setJobs(data.jobs);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setIsLoadingJobs(false);
    }
  }, [apiKey]);

  // Poll for job updates
  const startJobPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(() => {
      fetchJobs();
    }, 10000);
  };

  const handleThemeToggle = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    const root = document.documentElement;
    root.classList.remove('wbtws-theme-light', 'wbtws-theme-dark');
    root.classList.add(newTheme === 'light' ? 'wbtws-theme-light' : 'wbtws-theme-dark');
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.sample_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.job_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatStatusLabel = (status) => String(status || '').replace(/_/g, ' ');

  const formatJobName = (name) => String(name || 'Untitled WBTRisk job').replace(/^\s*\d+[\s._-]+/, '');

  const isDemoJob = (job) => Number(job?.demo) === 1;

  const getTodayInputValue = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDobValidationMessage = (dateString) => {
    if (!dateString) return 'Please enter a date of birth.';
    const dob = new Date(dateString + 'T00:00:00');
    if (Number.isNaN(dob.getTime())) return 'Please enter a valid date of birth.';
    const today = new Date(getTodayInputValue() + 'T00:00:00');
    if (dob > today) return 'Date of birth cannot be in the future.';
    const earliest = new Date(today);
    earliest.setFullYear(earliest.getFullYear() - 130);
    if (dob < earliest) return 'Date of birth is outside the accepted age range.';
    return '';
  };

  const calculateAgeFromDob = (dateString) => {
    if (!dateString) return '';
    const dob = new Date(dateString + 'T00:00:00');
    if (Number.isNaN(dob.getTime())) return '';
    const today = new Date();
    let years = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) years -= 1;
    return years >= 0 && years <= 130 ? String(years) : '';
  };

  const isFastqFile = (file) => /\.(fastq|fq)(\.gz)?$/i.test(file?.name || '');

  // File handling
  const handleFileSelect = (read, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isFastqFile(file)) {
      alert('Please select a .fastq.gz, .fq.gz, .fastq, or .fq file');
      e.target.value = '';
      return;
    }
    if (read === 'read1') setSelectedRead1(file);
    else setSelectedRead2(file);
  };

  const handleDrop = (read, e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!isFastqFile(file)) {
      alert('Please select a .fastq.gz, .fq.gz, .fastq, or .fq file');
      return;
    }
    if (read === 'read1') setSelectedRead1(file);
    else setSelectedRead2(file);
  };

  // Submit job: create -> upload R1/R2 -> confirm queued
  const handleSubmit = async () => {
    if (!sampleName || !gender || !age || !selectedRead1 || !selectedRead2 || !pipeline) {
      alert('Please fill in all required fields and select both FASTQ files.');
      return;
    }

    const dobError = getDobValidationMessage(age);
    if (dobError) {
      alert(dobError);
      return;
    }

    const calculatedAge = calculateAgeFromDob(age);

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      const progressByPosition = new Map([[1, 0], [2, 0]]);
      await createServiceUpload({
        serviceCode: SERVICE_CODE,
        token: apiKey,
        pipeline,
        sampleCode: pipeline.sample_codes?.[0],
        selectedFiles: [
          { file: selectedRead1, slotPosition: 1, label: 'Forward reads (R1)' },
          { file: selectedRead2, slotPosition: 2, label: 'Reverse reads (R2)' },
        ],
        patientParams: { age: Number(calculatedAge), sex: gender },
        displayName: sampleName,
        metadata: {
          sex: gender,
          gender,
          age: Number(calculatedAge),
          date_of_birth: age,
        },
        onProgress: ({ position, fraction }) => {
          progressByPosition.set(position, fraction);
          const combined = [...progressByPosition.values()].reduce((sum, value) => sum + value, 0) / 2;
          setUploadProgress(Math.round(combined * 100));
        },
      });

      await fetchJobs();
      setSampleName('');
      setGender('');
      setAge('');
      setSelectedRead1(null);
      setSelectedRead2(null);
      if (read1InputRef.current) read1InputRef.current.value = '';
      if (read2InputRef.current) read2InputRef.current.value = '';
    } catch (err) {
      console.error('Submit error:', err);
      alert(err.message);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  // Results modal state
  const [resultsJob, setResultsJob] = useState(null); // full job object or null

  const openResultsModal = (job) => {
    setResultsJob(job);
  };

  const closeResultsModal = () => {
    setResultsJob(null);
  };

  // Download all result files
  const handleDownloadResults = async () => {
    try {
      await openJobDownload(SERVICE_CODE, apiKey, resultsJob);
    } catch (err) {
      console.error('Download error:', err);
      alert(err.message);
    }
  };

  // Status helpers
  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return 'fa-spinner fa-spin';
      case 'completed': return 'fa-check';
      case 'failed': return 'fa-times';
      case 'queued': return 'fa-clock';
      case 'pending_upload': return 'fa-hourglass-start';
      case 'uploaded': return 'fa-upload';
      case 'cancelled': return 'fa-ban';
      default: return 'fa-circle';
    }
  };

  const logoSrc = theme === 'light' ? logoLight : logoDark;
  const baseLogoSrc = theme === 'light' ? baseLogoLight : baseLogo;
  const themeClass = theme === 'light' ? 'light-theme' : '';

  return (
    <div className={`wbtws-app-container ${themeClass}`}>
      {/* AppBar Header */}
      <header className="wbtws-header">
        <div className="wbtws-header-left">
          <a href="/" className="wbtws-logo-container">
            <img src={baseLogoSrc} alt="Home" className="wbtws-logo" />
          </a>
          <nav className="wbtws-nav-tabs">
            {['home', 'api', 'help', 'settings'].map(tab => (
              <button
                key={tab}
                className={`wbtws-nav-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="wbtws-content-area">
        {activeTab === 'settings' ? (
          <div className="wbtws-settings-container">
            <div className="wbtws-settings-content">
              <h1>Settings</h1>
              <div className="wbtws-settings-section">
                <h2><i className="fas fa-palette"></i> Appearance</h2>
                <div className="wbtws-setting-item">
                  <div className="wbtws-setting-info">
                    <h3>Theme</h3>
                    <p>Choose your preferred color scheme</p>
                  </div>
                  <div className="wbtws-theme-options">
                    <button className={`wbtws-theme-btn-setting ${theme === 'dark' ? 'active' : ''}`} onClick={() => handleThemeToggle('dark')}>
                      <i className="fas fa-moon"></i> Dark
                    </button>
                    <button className={`wbtws-theme-btn-setting ${theme === 'light' ? 'active' : ''}`} onClick={() => handleThemeToggle('light')}>
                      <i className="fas fa-sun"></i> Light
                    </button>
                  </div>
                </div>
              </div>
              <div className="wbtws-settings-section">
                <h2><i className="fas fa-info-circle"></i> About</h2>
                <div className="wbtws-about-info-content">
                  <div className="wbtws-about-item">
                    <span className="wbtws-about-label">Application</span>
                    <span className="wbtws-about-value">WBTRisk</span>
                  </div>
                  <div className="wbtws-about-item">
                    <span className="wbtws-about-label">Version</span>
                    <span className="wbtws-about-value">1.0.0</span>
                  </div>
                  <div className="wbtws-about-item">
                    <span className="wbtws-about-label">Account ID</span>
                    <span className="wbtws-about-value">{apiKey || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        ) : activeTab === 'help' ? (
          <div className="wbtws-help-page">
            <div className="wbtws-help-container">
              <div className="wbtws-help-header">
                <h1>Documentation</h1>
                <p>Everything you need to get started with WBTRisk</p>
              </div>

              {/* Quick Links */}
              <div className="wbtws-help-grid">
                <div className="wbtws-help-card">
                  <div className="wbtws-help-card-icon"><i className="fas fa-code"></i></div>
                  <div className="wbtws-help-card-content">
                    <h3>API Integration</h3>
                    <p>REST endpoints for programmatic access to WBTRisk</p>
                    <button className="wbtws-help-link" onClick={() => setActiveTab('api')}>
                      <i className="fas fa-arrow-right"></i> View API Docs
                    </button>
                  </div>
                </div>
                <div className="wbtws-help-card">
                  <div className="wbtws-help-card-icon"><i className="fas fa-envelope"></i></div>
                  <div className="wbtws-help-card-content">
                    <h3>Contact Support</h3>
                    <p>Get help from our team for technical issues or feedback</p>
                    <a href="mailto:hello@phenomelongevity.com" className="wbtws-help-link">
                      <i className="fas fa-paper-plane"></i> hello@phenomelongevity.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Getting Started Guide */}
              <div className="wbtws-docs-section">
                <h2>Getting Started</h2>
                <p className="wbtws-docs-intro">WBTRisk processes paired-end whole-blood transcriptome FASTQ files, queues analysis jobs, and returns downloadable result/report artifacts when processing is complete.</p>

                {/* Step 1 */}
                <div className="wbtws-docs-step">
                  <div className="wbtws-docs-step-header">
                    <span className="wbtws-docs-step-num">1</span>
                    <h3>Create a New Job</h3>
                  </div>
                  <p>From the Home tab, fill in the sample name, sex, and date of birth on the right panel. Then drag and drop both R1 and R2 <code>.fastq.gz</code> files into the upload zones and click <strong>Submit Job</strong>.</p>
                  <div className="wbtws-docs-screenshot">
                    <img src={docScreenshot1} alt="New WBTRisk Job form" />
                  </div>
                </div>

                {/* Step 2 */}
                <div className="wbtws-docs-step">
                  <div className="wbtws-docs-step-header">
                    <span className="wbtws-docs-step-num">2</span>
                    <h3>Monitor Job Progress</h3>
                  </div>
                  <p>Your job appears in the jobs list on the left. Status updates automatically every few seconds. A job goes through: <code>pending</code> → <code>queued</code> → <code>running</code> → <code>completed</code>.</p>
                  <div className="wbtws-docs-screenshot">
                    <img src={docScreenshot2} alt="Jobs list with running and completed jobs" />
                  </div>
                </div>

                {/* Step 3 */}
                <div className="wbtws-docs-step">
                  <div className="wbtws-docs-step-header">
                    <span className="wbtws-docs-step-num">3</span>
                    <h3>View Results & Select Language</h3>
                  </div>
                  <p>Click on a completed job to open the results panel and download the full result bundle.</p>
                  <div className="wbtws-docs-screenshot">
                    <img src={docScreenshot4} alt="Results modal with language selector" />
                  </div>
                </div>

                {/* Step 4 */}
                <div className="wbtws-docs-step">
                  <div className="wbtws-docs-step-header">
                    <span className="wbtws-docs-step-num">4</span>
                    <h3>Generate a Report</h3>
                  </div>
                  <p>Click Generate and the PDF renders inline once ready. The report includes clinical risk overviews, population-calibrated percentiles, and variant-level detail.</p>
                  <div className="wbtws-docs-screenshot">
                    <img src={docScreenshot3} alt="Report generation with PDF preview" />
                  </div>
                </div>
              </div>

              {/* Supported File Formats */}
              <div className="wbtws-docs-section">
                <h2>Supported Formats</h2>
                <div className="wbtws-docs-formats">
                  <div className="wbtws-docs-format-item">
                    <i className="fas fa-file-alt"></i>
                    <div>
                      <h4>.fastq.gz</h4>
                      <p>Paired R1/R2 FASTQ files are required. Compressed <code>.fastq.gz</code> or <code>.fq.gz</code> files are preferred.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Report Languages */}
              <div className="wbtws-docs-section">
                <h2>Report Languages</h2>
                <p>Reports can be generated in any of the following languages. Language is selected at report generation time, not during job submission.</p>
                <div className="wbtws-docs-lang-grid">
                  {['English', 'Turkish', 'Korean', 'Japanese', 'Chinese', 'Arabic', 'Spanish'].map(lang => (
                    <span key={lang} className="wbtws-docs-lang-tag">{lang}</span>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div className="wbtws-help-faq">
                <h2>Frequently Asked Questions</h2>
                <div className="wbtws-faq-list">
                  <div className="wbtws-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> What file formats are supported?</h4>
                    <p>WBTRisk accepts paired-end <code>.fastq.gz</code> or <code>.fq.gz</code> files for transcriptome processing.</p>
                  </div>
                  <div className="wbtws-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> How long does WBTRisk processing take?</h4>
                    <p>Processing time varies by file size. Most jobs complete within 15-45 minutes.</p>
                  </div>
                  <div className="wbtws-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> Is my data secure?</h4>
                    <p>Yes. All uploads are encrypted in transit and at rest. Data is processed in isolated environments and automatically deleted after analysis.</p>
                  </div>
                  <div className="wbtws-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> Can I generate reports in multiple languages?</h4>
                    <p>Yes. You can generate a new report for the same job in a different language at any time from the results panel.</p>
                  </div>
                  <div className="wbtws-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> What analysis methods are used?</h4>
                    <p>WBTRisk will run the configured transcriptome workflow and report pipeline for paired FASTQ inputs. Method details will be finalized with the pipeline/report implementation.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        ) : activeTab === 'api' ? (
          <CentralApiGuide
            serviceCode={SERVICE_CODE}
            serviceName="WBTRisk"
            token={apiKey}
          />
        ) : <div className="wbtws-home">
            <header className="wbtws-home-header">
              <div className="wbtws-welcome-container">
                <div className="wbtws-welcome-logo-wrapper">
                  <img src={logoSrc} alt="WBTRisk Logo" className="wbtws-welcome-logo" />
                </div>
                <div className="wbtws-welcome-text">
                  <h1>Welcome to WBTRisk</h1>
                  <p>Whole-blood transcriptome analysis platform</p>
                </div>
              </div>
            </header>

            <div className="wbtws-home-content">
              {/* Left Column - Jobs */}
              <section className="wbtws-left-column">
                <div className="wbtws-section wbtws-jobs-section">
                  <h2><i className="fas fa-tasks"></i> WBTRisk Jobs</h2>
                  <div className="wbtws-jobs-filter">
                    <div className="wbtws-search-input">
                      <i className="fas fa-search"></i>
                      <input type="text" placeholder="Search jobs..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <option value="all">All</option>
                      <option value="running">Running</option>
                      <option value="completed">Completed</option>
                      <option value="queued">Queued</option>
                      <option value="pending_upload">Pending Upload</option>
                      <option value="uploaded">Uploaded</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                  <div className="wbtws-jobs-list">
                    {isLoadingJobs ? (
                      <div className="wbtws-empty-state"><i className="fas fa-spinner fa-spin"></i><span>Loading...</span></div>
                    ) : filteredJobs.length === 0 ? (
                      <div className="wbtws-empty-state"><i className="fas fa-folder-open"></i><span>No jobs found</span></div>
                    ) : (
                      filteredJobs.map(job => (
                        <div key={job.job_id} className="wbtws-job-item" onClick={() => job.status === 'completed' ? openResultsModal(job) : null} style={{ cursor: job.status === 'completed' ? 'pointer' : 'default' }}>
                          <div className="wbtws-job-icon"><i className="fas fa-dna"></i></div>
                          <div className="wbtws-job-content">
                            <div className="wbtws-job-title">
                              {isDemoJob(job) && <span className="wbtws-demo-badge">DEMO</span>}
                              {formatJobName(job.sample_name)}
                            </div>
                            <div className="wbtws-job-subtitle">{job.gender} &middot; Age {job.age}</div>
                          </div>
                          <div className="wbtws-job-status-area">
                            <span className={`wbtws-job-type-badge ${job.status}`}>
                              <i className={`fas ${getStatusIcon(job.status)}`}></i> {formatStatusLabel(job.status)}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </section>

              {/* Right Column - New Job */}
              <aside className="wbtws-right-column">
                <div className="wbtws-section wbtws-newjob-section">
                  <h2><i className="fas fa-plus-circle"></i> New WBTRisk Job</h2>

                  <div className="wbtws-form-group">
                    <label>Sample Name <span className="wbtws-required">*</span></label>
                    <input type="text" placeholder="e.g. John Doe" value={sampleName} onChange={(e) => setSampleName(e.target.value)} disabled={isSubmitting} />
                  </div>

                  <div className="wbtws-form-row">
                    <div className="wbtws-form-group">
                      <label>Sex <span className="wbtws-required">*</span></label>
                      <select value={gender} onChange={(e) => setGender(e.target.value)} disabled={isSubmitting}>
                        <option value="">Select...</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Unknown">Unknown</option>
                      </select>
                    </div>
                    <div className="wbtws-form-group">
                      <label>Date of Birth <span className="wbtws-required">*</span></label>
                      <input type="date" value={age} max={getTodayInputValue()} onChange={(e) => setAge(e.target.value)} disabled={isSubmitting} />
                    </div>
                  </div>

                  <div className="wbtws-form-group">
                    <label>R1 FASTQ <span className="wbtws-required">*</span></label>
                    <div className={`wbtws-dropzone ${selectedRead1 ? 'has-file' : ''} ${isSubmitting ? 'is-uploading' : ''}`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { if (!isSubmitting) handleDrop('read1', e); else e.preventDefault(); }} onClick={() => { if (!isSubmitting) read1InputRef.current?.click(); }}>
                      <input type="file" ref={read1InputRef} onChange={(e) => handleFileSelect('read1', e)} accept=".fastq,.fastq.gz,.fq,.fq.gz,.gz,application/gzip,application/x-gzip" style={{ display: 'none' }} disabled={isSubmitting} />
                      {selectedRead1 ? (
                        <div className="wbtws-dropzone-file">
                          <i className="fas fa-file-alt"></i>
                          <div className="wbtws-dropzone-file-info">
                            <span className="wbtws-dropzone-filename">{selectedRead1.name}</span>
                            <span className="wbtws-dropzone-filesize">{formatFileSize(selectedRead1.size)}</span>
                          </div>
                          <button className="wbtws-dropzone-remove" disabled={isSubmitting} onClick={(e) => { e.stopPropagation(); if (isSubmitting) return; setSelectedRead1(null); if (read1InputRef.current) read1InputRef.current.value = ''; }}>
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="wbtws-dropzone-empty">
                          <i className="fas fa-cloud-upload-alt"></i>
                          <span>Drop R1 .fastq.gz here or click to browse</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="wbtws-form-group">
                    <label>R2 FASTQ <span className="wbtws-required">*</span></label>
                    <div className={`wbtws-dropzone ${selectedRead2 ? 'has-file' : ''} ${isSubmitting ? 'is-uploading' : ''}`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { if (!isSubmitting) handleDrop('read2', e); else e.preventDefault(); }} onClick={() => { if (!isSubmitting) read2InputRef.current?.click(); }}>
                      <input type="file" ref={read2InputRef} onChange={(e) => handleFileSelect('read2', e)} accept=".fastq,.fastq.gz,.fq,.fq.gz,.gz,application/gzip,application/x-gzip" style={{ display: 'none' }} disabled={isSubmitting} />
                      {selectedRead2 ? (
                        <div className="wbtws-dropzone-file">
                          <i className="fas fa-file-alt"></i>
                          <div className="wbtws-dropzone-file-info">
                            <span className="wbtws-dropzone-filename">{selectedRead2.name}</span>
                            <span className="wbtws-dropzone-filesize">{formatFileSize(selectedRead2.size)}</span>
                          </div>
                          <button className="wbtws-dropzone-remove" disabled={isSubmitting} onClick={(e) => { e.stopPropagation(); if (isSubmitting) return; setSelectedRead2(null); if (read2InputRef.current) read2InputRef.current.value = ''; }}>
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="wbtws-dropzone-empty">
                          <i className="fas fa-cloud-upload-alt"></i>
                          <span>Drop R2 .fastq.gz here or click to browse</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {isSubmitting && uploadProgress > 0 && (
                    <div className="wbtws-upload-progress">
                      <div className="wbtws-progress-bar">
                        <div className="wbtws-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                      </div>
                      <span className="wbtws-progress-text">{uploadProgress}%</span>
                    </div>
                  )}

                  <button className="wbtws-submit-btn" onClick={handleSubmit} disabled={isSubmitting || !sampleName || !gender || !age || !selectedRead1 || !selectedRead2 || !pipeline}>
                    {isSubmitting ? <><i className="fas fa-spinner fa-spin"></i> Submitting...</> : <><i className="fas fa-arrow-right"></i> Submit Job</>}
                  </button>
                </div>
                <WebserverDemoSection serviceCode={SERVICE_CODE} />
              </aside>
            </div>
          </div>}
      </main>

      <div className="wbtws-cookie-icon">
        <i className="fas fa-cookie"></i>
        <span className="wbtws-tooltip">{`ID: ${apiKey?.slice(0, 8)}...`}</span>
      </div>

      {/* Results Modal — FamilyRisk style */}
      {resultsJob && (
        <div className="wbtws-modal-overlay" onClick={closeResultsModal}>
          <div className="wbtws-results-modal" onClick={(e) => e.stopPropagation()}>

            {/* LEFT SIDEBAR */}
            <div className="wbtws-results-sidebar">
              {/* Job info */}
              <div className="wbtws-results-jobinfo">
                <div className="wbtws-results-jobinfo-header">
                  <i className="fas fa-dna"></i>
                  <span>{formatJobName(resultsJob.sample_name)}</span>
                  <button className="wbtws-modal-close" onClick={closeResultsModal}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="wbtws-results-jobinfo-rows">
                  <div className="wbtws-results-jobinfo-row">
                    <span className="wbtws-results-jobinfo-label">Status</span>
                    <span className="wbtws-results-jobinfo-value">
                      <span className={`wbtws-job-type-badge ${resultsJob.status}`}>
                        <i className={`fas ${getStatusIcon(resultsJob.status)}`}></i> {formatStatusLabel(resultsJob.status)}
                      </span>
                    </span>
                  </div>
                  <div className="wbtws-results-jobinfo-row">
                    <span className="wbtws-results-jobinfo-label">Gender</span>
                    <span className="wbtws-results-jobinfo-value">{resultsJob.gender}</span>
                  </div>
                  <div className="wbtws-results-jobinfo-row">
                    <span className="wbtws-results-jobinfo-label">Age</span>
                    <span className="wbtws-results-jobinfo-value">{resultsJob.age}</span>
                  </div>
                  {resultsJob.created_at && (
                    <div className="wbtws-results-jobinfo-row">
                      <span className="wbtws-results-jobinfo-label">Created</span>
                      <span className="wbtws-results-jobinfo-value">{new Date(resultsJob.created_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="wbtws-results-jobinfo-row">
                    <span className="wbtws-results-jobinfo-label">Job ID</span>
                    <span className="wbtws-results-jobinfo-value wbtws-results-jobid">{resultsJob.job_id}</span>
                  </div>
                </div>
              </div>

              {/* Footer actions */}
              <div className="wbtws-results-sidebar-footer">
                <button className="wbtws-results-download-btn" onClick={handleDownloadResults} title="Download all results">
                  <i className="fas fa-download"></i> Download all results
                </button>
              </div>
            </div>

            {/* RIGHT PANEL */}
            <div className="wbtws-results-preview">
              <div className="wbtws-results-empty">
                <i className="fas fa-chart-bar"></i>
                <span>WBTRisk results are ready</span>
                <p>Download the complete output bundle generated by the active WBTRisk pipeline.</p>
              </div>
              <WebserverReportPanel
                serviceCode={SERVICE_CODE}
                token={apiKey}
                job={resultsJob}
                reportTypes={pipeline?.report_types || []}
              />
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default WBT;
